import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../api";

const EASE = [0.22, 1, 0.36, 1];

const COPY = {
  teacher: {
    label: "Certified teachers",
    sub: "Teachers in our network — independent professionals certified to teach with us.",
    empty: "No certified teachers listed yet. Check back soon.",
    testid: "partnerships-teachers-page",
  },
  organization: {
    label: "Organizations",
    sub: "Schools, foundations, and cultural partners we collaborate with.",
    empty: "No organizations listed yet. Check back soon.",
    testid: "partnerships-organizations-page",
  },
};

export default function PartnershipsList({ kind }) {
  const meta = COPY[kind];
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.listPartnerships(kind)
      .then((d) => { if (!cancelled) setItems(d || []); })
      .catch((e) => { if (!cancelled) setError(e.message || "Could not load."); });
    return () => { cancelled = true; };
  }, [kind]);

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-16 px-6 md:px-10"
      data-testid={meta.testid}
    >
      <div className="max-w-6xl mx-auto">
        <Link
          to="/partnerships"
          className="text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 hover:text-[#1C1C1C] transition-colors"
        >
          ← Partnerships
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mt-4"
        >
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
            Partnerships · {meta.label}
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tighter font-light text-[#1C1C1C]">
            {meta.label}.
          </h1>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-[#1C1C1C]/70 leading-relaxed">
            {meta.sub}
          </p>
        </motion.div>

        <div className="mt-12">
          {error && (
            <div className="px-4 py-3 bg-[#B83A3A]/10 text-[#B83A3A] rounded-xl text-sm">
              {error}
            </div>
          )}
          {items === null ? (
            <SkeletonGrid />
          ) : items.length === 0 ? (
            <div
              data-testid="partnerships-empty"
              className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-10 text-center text-[#1C1C1C]/60"
            >
              {meta.empty}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
              {items.map((it, idx) => (
                <Card key={it.id} item={it} delay={0.05 + idx * 0.04} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Card({ item, delay }) {
  const Wrap = item.link_url ? "a" : "div";
  const wrapProps = item.link_url
    ? { href: item.link_url, target: "_blank", rel: "noopener noreferrer" }
    : {};
  // Logos (transparent PNGs / SVGs) deserve a centred, padded treatment.
  // Photos (JPEG/WEBP) keep the edge-to-edge cover crop.
  const isLogo =
    !!item.image_data_url &&
    /^data:image\/(png|svg\+xml)/i.test(item.image_data_url);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE, delay }}
    >
      <Wrap
        {...wrapProps}
        data-testid={`partnership-card-${item.id}`}
        className={`group block bg-white border border-[#1C1C1C]/10 rounded-2xl overflow-hidden ${
          item.link_url ? "hover:border-[#1C1C1C]/30 cursor-pointer" : ""
        } transition-colors`}
      >
        <div className="relative aspect-[4/3] bg-[#FAF9F6] overflow-hidden">
          {item.image_data_url ? (
            isLogo ? (
              <div className="absolute inset-0 flex items-center justify-center p-8 md:p-10 bg-[radial-gradient(ellipse_at_top,_#F2EFE3_0%,_#FAF9F6_70%)]">
                <img
                  src={item.image_data_url}
                  alt={item.name}
                  className="max-w-[78%] max-h-[78%] object-contain transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </div>
            ) : (
              <img
                src={item.image_data_url}
                alt={item.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
              />
            )
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#1C1C1C]/30">
              <span className="text-[10px] tracking-[0.32em] uppercase">No image</span>
            </div>
          )}
        </div>
        <div className="p-5 md:p-6">
          <div className="font-display text-xl text-[#1C1C1C] leading-tight">{item.name}</div>
          {item.about && (
            <p className="mt-2.5 text-sm text-[#1C1C1C]/70 leading-relaxed line-clamp-4">
              {item.about}
            </p>
          )}
          {item.link_url && (
            <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C] group-hover:text-[#7FA6C8] transition-colors">
              Visit website
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </div>
          )}
        </div>
      </Wrap>
    </motion.div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7" aria-hidden>
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white border border-[#1C1C1C]/10 rounded-2xl overflow-hidden animate-pulse">
          <div className="aspect-[4/3] bg-[#1C1C1C]/5" />
          <div className="p-5 space-y-3">
            <div className="h-5 bg-[#1C1C1C]/10 rounded w-2/3" />
            <div className="h-3 bg-[#1C1C1C]/10 rounded w-full" />
            <div className="h-3 bg-[#1C1C1C]/10 rounded w-4/5" />
          </div>
        </div>
      ))}
    </div>
  );
}
